/**
 * BreachInvestigationPage
 * Route: /admin/breach/:id
 *
 * Breach investigation dashboard with 72-hour countdown,
 * timeline tracker, and DPB/user notification builders.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Users,
  FileText,
  ArrowLeft,
  Loader2,
  Bell,
  Lock,
  Loader,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { getBreachDetails, updateBreachStatus, acknowledgeBreach } from "@/services/dpdpApi";

const STATUS_STEPS = [
  { key: "detected", label: "Detected", icon: ShieldAlert, color: "bg-gray-500" },
  { key: "investigating", label: "Investigating", icon: Loader, color: "bg-blue-500" },
  { key: "contained", label: "Contained", icon: Lock, color: "bg-orange-500" },
  { key: "notified", label: "DPB Notified", icon: Bell, color: "bg-amber-500" },
  { key: "resolved", label: "Resolved", icon: CheckCircle, color: "bg-green-500" },
  { key: "closed", label: "Closed", icon: CheckCircle, color: "bg-emerald-600" },
];

const STATUS_ORDER = ["detected", "investigating", "contained", "notified", "resolved", "closed"];

function calculateCountdown(dueBy) {
  const now = new Date();
  const due = new Date(dueBy);
  const msLeft = due - now;
  if (msLeft <= 0) return { overdue: true, hours: 0, minutes: 0, total: 0 };
  const hours = Math.floor(msLeft / (1000 * 60 * 60));
  const minutes = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
  return { overdue: false, hours, minutes, total: msLeft };
}

const TOTAL_72H_MS = 72 * 60 * 60 * 1000;

function getSeverityColor(severity) {
  switch (severity) {
    case "critical": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    case "high": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
    case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "low": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    default: return "bg-gray-100 text-gray-800";
  }
}

function getStatusColor(status) {
  switch (status) {
    case "detected": return "bg-gray-500";
    case "investigating": return "bg-blue-500";
    case "contained": return "bg-orange-500";
    case "notified": return "bg-amber-500";
    case "resolved": return "bg-green-500";
    case "closed": return "bg-emerald-600";
    default: return "bg-gray-500";
  }
}

export default function BreachInvestigationPage() {
  const { id: breachId } = useParams();
  const navigate = useNavigate();

  const [breach, setBreach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [updating, setUpdating] = useState(false);
  const [dpbTitle, setDpbTitle] = useState("");
  const [dpbBody, setDpbBody] = useState("");
  const [userTitle, setUserTitle] = useState("");
  const [userBody, setUserBody] = useState("");
  const [includeUserNotif, setIncludeUserNotif] = useState(true);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    loadBreach();
    return () => countdownInterval && clearInterval(countdownInterval);
  }, [breachId]);

  useEffect(() => {
    if (breach?.due_by_dpb) {
      const tick = () => setCountdown(calculateCountdown(breach.due_by_dpb));
      tick();
      const interval = setInterval(tick, 30000);
      setCountdownInterval(interval);
    }
  }, [breach?.due_by_dpb]);

  const loadBreach = async () => {
    setLoading(true);
    try {
      const data = await getBreachDetails(breachId);
      setBreach(data);
      if (data.dpb_notification) {
        setDpbTitle(data.dpb_notification.title || "");
        setDpbBody(data.dpb_notification.body || "");
        setUserTitle(data.dpb_notification.title || "");
        setUserBody(data.dpb_notification.user_notification_body || "");
      }
    } catch (err) {
      toast.error("Failed to load breach details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const payload = {
        status: newStatus,
        notes: notes || null,
        dpb_notification: newStatus === "notified" ? {
          breach_id: breachId,
          title: dpbTitle || `Breach Notification: ${breach?.breach_type}`,
          body: dpbBody || "Breach notification details as per DPDP Act 2023 Section 8(6)",
          include_user_notification: includeUserNotif,
          user_notification_body: includeUserNotif ? (userBody || "Your data may have been affected. We are investigating.") : null,
        } : null,
      };
      await updateBreachStatus(breachId, payload);
      toast.success(`Breach status updated to: ${newStatus}`);
      await loadBreach();
      setNotes("");
    } catch (err) {
      toast.error(err.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleAcknowledge = async () => {
    setUpdating(true);
    try {
      await acknowledgeBreach(breachId, { notes: notes || null });
      toast.success("Breach acknowledged — investigation started");
      await loadBreach();
      setNotes("");
    } catch (err) {
      toast.error(err.message || "Failed to acknowledge breach");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950/20 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-red-400" />
      </div>
    );
  }

  if (!breach) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950/20 to-slate-900 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center py-8">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Breach Not Found</h2>
            <Button variant="outline" onClick={() => navigate("/admin/breach")}>
              Back to Breach List
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStatusIdx = STATUS_ORDER.indexOf(breach.status);
  const progressPct = ((currentStatusIdx + 1) / STATUS_ORDER.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950/20 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate("/admin/breach")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-base font-bold text-white flex items-center gap-2 truncate">
              <ShieldAlert className="h-4 w-4 text-red-400 flex-shrink-0" />
              Breach Investigation
              <Badge className={getSeverityColor(breach.severity)}>{breach.severity}</Badge>
            </h1>
            <p className="text-xs text-white/50 font-mono truncate">{breach.breach_id}</p>
          </div>
          {countdown && (
            <div className={`text-right flex-shrink-0 ${countdown.overdue ? "text-red-400" : "text-white"}`}>
              <div className="flex items-center gap-1 text-xs text-white/50">
                <Clock className="h-3 w-3" />
                DPB Deadline
              </div>
              <div className="font-mono font-bold text-sm">
                {countdown.overdue ? (
                  <span className="text-red-400">OVERDUE</span>
                ) : (
                  `${countdown.hours}h ${countdown.mindown}m`
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* 72-hour countdown card */}
        {countdown && !breach.dpb_overdue && breach.status !== "notified" && breach.status !== "resolved" && breach.status !== "closed" && (
          <Card className="border-amber-500/30 bg-amber-950/10">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-300">
                    DPB Notification Deadline
                  </span>
                </div>
                <span className="font-mono text-sm font-bold text-amber-300">
                  {countdown.overdue ? "OVERDUE" : `${countdown.hours}h ${countdown.minutes}m remaining`}
                </span>
              </div>
              <Progress
                value={Math.min(100, (countdown.total / TOTAL_72H_MS) * 100)}
                className="h-2 [&>div]:bg-amber-500"
              />
              <p className="text-xs text-amber-200/60 mt-2">
                Notify the Data Protection Board within 72 hours of detection per DPDP Section 8(6)
              </p>
            </CardContent>
          </Card>
        )}

        {/* Timeline tracker */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Breach Lifecycle</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-0">
              {STATUS_STEPS.map((step, i) => {
                const isCompleted = i <= currentStatusIdx;
                const isCurrent = i === currentStatusIdx;
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted ? step.color : "bg-white/10"
                      } ${isCurrent ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900" : ""}`}>
                        <Icon className={`h-5 w-5 ${isCompleted ? "text-white" : "text-white/40"}`} />
                      </div>
                      <p className={`text-xs mt-1 text-center ${isCompleted ? "text-white" : "text-white/40"}`}>
                        {step.label}
                      </p>
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div className={`h-0.5 flex-1 ${i < currentStatusIdx ? "bg-green-500" : "bg-white/10"} mx-1`} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <Progress value={progressPct} className="h-1.5 [&>div]:bg-indigo-500" />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10">
          {["overview", "notification", "timeline", "actions"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 ${
                activeTab === tab
                  ? "border-indigo-500 text-white"
                  : "border-transparent text-white/50 hover:text-white/80"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="py-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Type</span>
                  <span className="font-medium text-white">{breach.breach_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Severity</span>
                  <Badge className={getSeverityColor(breach.severity)}>{breach.severity}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Status</span>
                  <Badge style={{ backgroundColor: getStatusColor(breach.status).replace("bg-", "") }} className={getStatusColor(breach.status)}>
                    {breach.status}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Detected</span>
                  <span className="text-white">{new Date(breach.detected_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Affected Users</span>
                  <span className="font-medium text-white flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {breach.estimated_affected_users.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Due by DPB</span>
                  <span className={breach.dpb_overdue ? "text-red-400 font-bold" : "text-white"}>
                    {new Date(breach.due_by_dpb).toLocaleString()}
                    {breach.dpb_overdue && " — OVERDUE"}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-white/80">{breach.description}</p>
                {breach.source_details && (
                  <p className="text-xs text-white/40 mt-2">Source: {breach.source_details}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Affected Data Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {breach.affected_data_categories.map((cat) => (
                    <Badge key={cat} variant="outline" className="border-red-500/50 text-red-300">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Containment Steps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {breach.containment_steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{step}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Notification Builder Tab */}
        {activeTab === "notification" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-400" />
                  DPB Notification Draft
                </CardTitle>
                <CardDescription>
                  Draft the notification to be submitted to the Data Protection Board
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white/70">Notification Title</Label>
                  <Input
                    value={dpbTitle}
                    onChange={(e) => setDpbTitle(e.target.value)}
                    placeholder="Breach Notification: [brief description]"
                    className="mt-1 bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Notification Body</Label>
                  <Textarea
                    value={dpbBody}
                    onChange={(e) => setDpbBody(e.target.value)}
                    placeholder="Describe the breach, affected data categories, estimated users, containment steps taken, and remedial measures..."
                    className="mt-1 h-40 bg-white/10 border-white/20 text-white resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">User Notification Draft</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={includeUserNotif}
                    onChange={(e) => setIncludeUserNotif(e.target.checked)}
                    className="rounded"
                  />
                  Notify affected users individually
                </label>
                {includeUserNotif && (
                  <>
                    <div>
                      <Label className="text-white/70">User Notification Title</Label>
                      <Input
                        value={userTitle}
                        onChange={(e) => setUserTitle(e.target.value)}
                        className="mt-1 bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">User Notification Body</Label>
                      <Textarea
                        value={userBody}
                        onChange={(e) => setUserBody(e.target.value)}
                        placeholder="We are writing to inform you that a data security incident may have affected your account..."
                        className="mt-1 h-32 bg-white/10 border-white/20 text-white resize-none"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Button
              className="w-full bg-amber-600 hover:bg-amber-700"
              onClick={() => handleStatusUpdate("notified")}
              disabled={updating || breach.status === "notified" || breach.status === "resolved" || breach.status === "closed"}
            >
              {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
              {breach.status === "notified" ? "DPB Already Notified" : "Mark as Notified — Submit DPB Notification"}
            </Button>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === "timeline" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Breach Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[...(breach.timeline || [])].reverse().map((entry, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-indigo-500 mt-1.5" />
                      {i < breach.timeline.length - 1 && (
                        <div className="w-0.5 flex-1 bg-white/10 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm text-white">{entry.action.replace(/_/g, " ")}</p>
                        <span className="text-xs text-white/40">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-white/70 mt-0.5">{entry.description}</p>
                      <p className="text-xs text-white/30 mt-0.5">by {entry.actor}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions Tab */}
        {activeTab === "actions" && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status Actions</CardTitle>
                <CardDescription>Update the breach lifecycle status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white/70">Notes (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this status change..."
                    className="mt-1 h-20 bg-white/10 border-white/20 text-white resize-none"
                  />
                </div>

                {breach.status === "detected" && (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleAcknowledge}
                    disabled={updating}
                  >
                    {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Loader className="h-4 w-4 mr-2" />}
                    Acknowledge & Start Investigation
                  </Button>
                )}

                {breach.status === "investigating" && (
                  <Button
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    onClick={() => handleStatusUpdate("contained")}
                    disabled={updating}
                  >
                    {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                    Mark as Contained
                  </Button>
                )}

                {breach.status === "contained" && (
                  <Button
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={() => handleStatusUpdate("notified")}
                    disabled={updating}
                  >
                    {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
                    Mark as Notified (DPB + Users)
                  </Button>
                )}

                {breach.status === "notified" && (
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleStatusUpdate("resolved")}
                    disabled={updating}
                  >
                    {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Mark as Resolved
                  </Button>
                )}

                {breach.status === "resolved" && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleStatusUpdate("closed")}
                    disabled={updating}
                  >
                    {updating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Close Breach
                  </Button>
                )}

                {["resolved", "closed"].includes(breach.status) && (
                  <p className="text-center text-sm text-green-400">
                    Breach is {breach.status}. Post-incident monitoring is active.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
